import React, { useContext, useEffect } from 'react';
import Navigation from '../components/header-and-footer/header/Navigation';
import { Footer } from '../components/header-and-footer/footer/Footer';
import { SkipToContent } from '@entur/a11y';
import { Grid } from '@entur/layout/beta';
import { FormattedMessage } from 'react-intl';
import { createMedia } from '@artsy/fresnel';
import {
  CONSENT_UPDATED_EVENT,
  ConsentDetails,
  Consents,
  formatConsentEvent,
  waitFor,
} from '../utils/cmpUtils';

import { breakpoints } from '../styles/breakpoints';
import '../styles/OmturGrid.scss';

export const { MediaContextProvider, Media } = createMedia({
  breakpoints: {
    mobile: 0,
    desktop: 649,
    largeDesktop: breakpoints.desktop,
  },
});

type LayoutProps = {
  children: React.ReactNode;
  isContrast?: boolean;
  className?: string;
  style?: React.CSSProperties;
};

const MainGrid = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithRef<typeof Grid>
>((props, ref) => (
  <Grid
    ref={ref}
    as="main"
    templateColumns={{
      base: 'repeat(4, 1fr)',
      m: 'repeat(8, 1fr)',
      lg: 'repeat(12, 1fr)',
    }}
    templateRows="auto auto"
    columnGap={{ base: 's-m', m: 'm-l' }}
    style={{ marginInline: '1rem' }}
    {...props}
  />
));

const MainWrapper = ({ children, isContrast = false, style }: LayoutProps) => {
  useEffect(() => {
    let previousConsents: Consents | null = null;
    const POSTHOG_SERVICE_NAME = 'PostHog.com';

    async function handleConsentUpdate(
      event: Event & { detail?: ConsentDetails }
    ) {
      if (typeof window === 'undefined') return;

      const consents = formatConsentEvent(event);
      const posthogConsent = consents?.find(
        (c) => c.name === POSTHOG_SERVICE_NAME
      );

      if (posthogConsent?.consentGiven) {
        await waitFor(() => window.posthog !== undefined);
        // @ts-expect-error identify does exist on posthog
        window.posthog?.identify(event.detail?.consent.controllerId);
        // @ts-expect-error opt_in_capturing does exist on posthog
        window.posthog?.opt_in_capturing();
      }

      if (previousConsents !== null) {
        const previousPostHogConsent = previousConsents?.find(
          (c) => c.name === POSTHOG_SERVICE_NAME
        );

        const posthogDeclined =
          previousPostHogConsent?.consentGiven === true &&
          posthogConsent?.consentGiven === false;

        if (posthogDeclined) location.reload();
      }

      previousConsents = consents;
    }

    window.addEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);

    return () =>
      window.removeEventListener(CONSENT_UPDATED_EVENT, handleConsentUpdate);
  }, []);

  return (
    <>
      <MediaContextProvider>
        <SkipToContent>
          <FormattedMessage id="skipToContent" />
        </SkipToContent>
        <Grid
          className="omtur"
          templateColumns="minmax(1rem, auto) minmax(20rem, 60rem) [main] minmax(1rem, auto)"
          templateRows="fit-content(6.75rem) [topbar] auto"
          gap="none"
          style={{ width: '100%' }}
          height="100dvh"
        >
          <Navigation isContrast={isContrast} />
          <Grid.Item
            as={MainGrid}
            id="main-content"
            colSpan="2 / -2"
            rowSpan="2 / -1"
            style={{ ...style }}
          >
            {children}
          </Grid.Item>
          <Footer />
        </Grid>
      </MediaContextProvider>
    </>
  );
};

export const LayoutWithoutGrid = ({
  children,
  isContrast = false,
}: LayoutProps) => {
  React.useMemo(() => {}, []);

  return (
    <MediaContextProvider>
      <SkipToContent>
        <FormattedMessage id="skipToContent" />
      </SkipToContent>
      <Navigation isContrast={isContrast} />
      {children}
      <Footer />
    </MediaContextProvider>
  );
};

export default MainWrapper;
